// src/lib/errorReporter.js
// Global frontend error capture → error_log (Cloud SQL, via db-query).
// db-query emits a Cloud Error Reporting event for every error_log insert,
// so anything reported here alerts in GCP Error Reporting (BAA-covered)
// under service 'epiphany-frontend'.
//
// Flood protection: per-message dedupe (60s) + hard session cap, so an error
// in a render loop can't hammer db-query. Production builds only.
// PHI hygiene: URLs are stripped of query strings and UUID path segments
// before leaving the browser.

import { db } from './db';

const DEDUPE_MS       = 60 * 1000;
const MAX_PER_SESSION = 20;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const seen = new Map(); // message key -> last reported ts
let reportedCount = 0;

function scrubUrl(u) {
  return String(u || '').split('?')[0].replace(UUID_RE, ':id');
}

export function reportError(message, stack, boundary = 'window') {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    if (reportedCount >= MAX_PER_SESSION) return;

    const key = String(message).slice(0, 200);
    const now = Date.now();
    if (seen.has(key) && now - seen.get(key) < DEDUPE_MS) return;
    seen.set(key, now);
    reportedCount += 1;

    db.from('error_log').insert([{
      message:     String(message || 'Unknown error').slice(0, 2000),
      stack:       String(stack || '').slice(0, 8000),
      boundary,
      url:         scrubUrl(window.location.href),
      occurred_at: new Date().toISOString(),
    }]).catch(() => {});
  } catch (_) {
    // The reporter must never throw.
  }
}

export function installGlobalErrorReporter() {
  window.addEventListener('error', (e) => {
    // Ignore cross-origin "Script error." noise with no useful signal.
    if (e.message === 'Script error.' && !e.filename) return;
    reportError(e.message || 'Uncaught error', e.error?.stack, 'window.onerror');
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportError(
      e.reason?.message || 'Unhandled promise rejection',
      e.reason?.stack,
      'unhandledrejection',
    );
  });
}
